import { useSearchParams } from 'react-router-dom'
import {
  Wrench,
  LayoutTemplate,
  FileText,
  Boxes,
  CircleDashed,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { usePermission } from '@/lib/permissions'
import { PERMISSIONS as P } from '@/lib/permissions'
import { FEATURES as F } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { FormDefinitionsPage } from './FormDefinitionsPage'
import { ProcedureTemplatesPage } from './ProcedureTemplatesPage'
import { FieldLibraryPage } from './FieldLibraryPage'
import { ProcessPage } from './ProcessPage'
import { PageHeading } from '@/components/layout/PageHeading'

// ─── Tab config ────────────────────────────────────────────────────────────────

type TabValue = 'templates' | 'forms' | 'form-sequence' | 'process' | 'fields'

interface TabDef {
  value: TabValue
  label: string
  icon: React.ElementType
}

const ALL_TABS: TabDef[] = [
  { value: 'templates', label: 'Procedure Templates', icon: LayoutTemplate },
  { value: 'forms', label: 'Forms', icon: FileText },
  { value: 'fields', label: 'Fields', icon: Boxes },
  { value: 'process', label: 'Process', icon: CircleDashed }
]

// ─── Hub page ────────────────────────────────────────────────────────────────────

export function FormBuilderPage() {
  const [params, setParams] = useSearchParams()
  const { canAny } = usePermission()
  // The templates and form-sequence tabs render ProcedureTemplatesPage, which is
  // template-management UI. Gate them behind template perms so users with
  // only forms perms can't reach procedure templates through the form-builder entry.
  const canSeeProcedureTemplates = canAny([P.procedureTemplatesRead, P.procedureTemplatesManage])

  // SOFT feature gates. The route itself is hard-gated on `formEngine`, so
  // reaching this page means the builder is available; these only drop the tabs
  // whose backing module is switched off. Losing the Fields tab must never take
  // the whole builder with it — that is why these live here and not in the
  // router's FeatureGated list.
  const authoringOn = useFeatureFlag(F.authoring)
  // Both the Templates and Process tabs are procedure-template surfaces: Templates
  // renders ProcedureTemplatesPage, Process drives the step DAG through
  // processStepsApi, and both sit behind the `procedure-templates` key on the backend.
  const procedureTemplatesOn = useFeatureFlag(F.procedureTemplates)

  const TABS = ALL_TABS.filter((t) => {
    if (t.value === 'templates') return canSeeProcedureTemplates && procedureTemplatesOn
    if (t.value === 'process') return procedureTemplatesOn
    if (t.value === 'fields') return authoringOn
    return true
  })
  const VALID_TABS = new Set(TABS.map((t) => t.value))

  const requested = params.get('tab') as TabValue | null
  // Fall back to the first tab that survived filtering rather than a hardcoded
  // one, so a disabled module can't leave the page on a tab that no longer
  // renders. 'forms' is the backstop: it is gated on `formEngine`, which the
  // route already guaranteed.
  const fallbackTab: TabValue = TABS[0]?.value ?? 'forms'
  const tab: TabValue =
    requested && VALID_TABS.has(requested) ? requested : fallbackTab

  const setTab = (value: string) => {
    setParams(
      prev => {
        prev.set('tab', value)
        // Leaving the Process tab drops its selection — coming back should
        // show the process list, not a stale field (and its unsaved drafts).
        if (value !== 'process') {
          prev.delete('field')
          prev.delete('step')
        }
        return prev
      },
      { replace: true },
    )
  }

  return (
    <div className='flex h-full flex-col overflow-hidden bg-background'>
      <Tabs
        value={tab}
        onValueChange={setTab}
        className='flex min-h-0 flex-1 flex-col overflow-hidden'
      >
        {/* Header + tab bar share one clean band */}
        <header className='shrink-0 border-b bg-background px-6 pt-5'>
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10'>
              <Wrench className='h-5 w-5 text-primary' />
            </div>
            <PageHeading title='Form Builder' description='Manage templates, forms, and their sequencing.' />
          </div>

          {/* Underline tabs */}
          <TabsList className='mt-4 h-auto w-full justify-start gap-1 rounded-none border-0 bg-transparent p-0'>
            {TABS.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                className={cn(
                  'group relative gap-2 rounded-none border-b-2 border-transparent bg-transparent px-3.5 pb-3 pt-2 text-sm font-medium text-muted-foreground',
                  'transition-colors hover:text-foreground',
                  'data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none',
                )}
              >
                <Icon className='h-4 w-4 opacity-70 group-data-[state=active]:opacity-100' />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </header>

        {/* Tab content */}
        <div className='min-h-0 flex-1 overflow-y-auto bg-muted/20'>
          {canSeeProcedureTemplates && procedureTemplatesOn && (
            <TabsContent value='templates' className='mt-0 w-full'>
              <ProcedureTemplatesPage />
            </TabsContent>
          )}
          <TabsContent value='forms' className='mt-0 w-full'>
            <FormDefinitionsPage />
          </TabsContent>
          {procedureTemplatesOn && (
            <TabsContent value='process' className='mt-0 w-full'>
              <ProcessPage />
            </TabsContent>
          )}
          {canSeeProcedureTemplates && procedureTemplatesOn && (
            <TabsContent value='form-sequence' className='mt-0 w-full'>
              <ProcedureTemplatesPage />
            </TabsContent>
          )}
          {authoringOn && (
            <TabsContent value='fields' className='mt-0 w-full'>
              <FieldLibraryPage />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  )
}
